var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { theme } from '../../config';
import { MULTISKILL_COLOR } from '../annual/constants';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { QueueType } from '../multiskill/types';
import { SeatExceededIcon, useSeatCapacity } from '../seatCapacity';
import { DailyMetricHierarchyRows, FixedTableBanner, LEVEL_ROW_INDENTS, LEVEL_ROW_STICKIES, StyledDailyHeaderCell, StyledDateLink, StyledLevelFirstCell, buildIntervalUrl, updateUniqueIdSearchParam } from './common';
import { CLOSED_INDICATOR } from './constants';
import { useDailyContext } from './context';
import { ContentNav, StyledAutocomplete, StyledJumpTo, TableNavBar } from './detailTable';
import { useDailySeatExceeded } from './seatExceeded';
import { isDailyQueueData, useDailyAPI, useDailyReportPlan, useDaysOfWeek, usePlanColor, useReportTimelineOptions, useVisibleMetrics } from './selectors';
var getRowColor = function (rowKind, isTotalRow, bodyColor, isMultiskill) {
  if (isMultiskill === void 0) {
    isMultiskill = false;
  }
  if (isMultiskill) return MULTISKILL_COLOR;
  if (rowKind === 'location') return theme.palette.background.default;
  if (isTotalRow) return bodyColor;
  return undefined;
};
export var DayTable = function (_a) {
  var _b;
  var daysOfWeek = _a.daysOfWeek,
    handleDateClick = _a.handleDateClick,
    visibleMetrics = _a.visibleMetrics,
    detail = _a.detail,
    countries = _a.countries,
    queuesByLob = _a.queuesByLob,
    headerColor = _a.headerColor,
    bodyColor = _a.bodyColor;
  var t = useTranslation(['report', 'translation']).t;
  var isDayExceeded = useDailySeatExceeded().isDayExceeded;
  var openAlertDetails = useSeatCapacity().openAlertDetails;
  var visibleDayIndices = React.useMemo(function () {
    return daysOfWeek.map(function (__day, index) {
      return index;
    });
  }, [daysOfWeek]);
  var getStaffHours = function (source, metricKey) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (metricKey === 'workVolume') {
      return (_d = (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a['total']) === null || _b === void 0 ? void 0 : _b['staffHours']) === null || _c === void 0 ? void 0 : _c[0]) !== null && _d !== void 0 ? _d : '';
    }
    if (metricKey === 'productionFte') {
      return (_h = (_g = (_f = (_e = source.detail) === null || _e === void 0 ? void 0 : _e['total']) === null || _f === void 0 ? void 0 : _f['staffHours']) === null || _g === void 0 ? void 0 : _g[1]) !== null && _h !== void 0 ? _h : '';
    }
    return '';
  };
  var renderValueCells = function (source, metric, rowKind) {
    var _a, _b, _c;
    return _jsxs(_Fragment, {
      children: [visibleDayIndices.map(function (dayIndex) {
        var _a, _b, _c;
        var value = (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a[dayIndex]) === null || _b === void 0 ? void 0 : _b['total']) === null || _c === void 0 ? void 0 : _c[metric.idx];
        return _jsx(TableCell, {
          value: value !== null && value !== void 0 ? value : CLOSED_INDICATOR,
          decimal: metric.decimal,
          percentage: metric.percentage,
          color: getRowColor(rowKind, source.detail === detail, bodyColor, source.queueType === QueueType.Multiskill || source.isMultiskillMember),
          align: "center"
        }, dayIndex);
      }), _jsx(TableCell, {
        value: (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a['total']) === null || _b === void 0 ? void 0 : _b['total']) === null || _c === void 0 ? void 0 : _c[metric.idx],
        decimal: metric.decimal,
        percentage: metric.percentage,
        color: bodyColor,
        align: "center"
      }), _jsx(TableCell, {
        value: getStaffHours(source, metric.key),
        decimal: 1,
        color: bodyColor,
        align: "center"
      })]
    });
  };
  var renderBlankCells = function (rowColor) {
    return _jsxs(_Fragment, {
      children: [visibleDayIndices.map(function (dayIndex) {
        return _jsx(TableCell, {
          color: rowColor
        }, dayIndex);
      }), _jsx(TableCell, {
        color: rowColor
      }), _jsx(TableCell, {
        color: rowColor
      })]
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsx(FixedTableBanner, {
      children: (_b = daysOfWeek[0]) === null || _b === void 0 ? void 0 : _b.label
    }), _jsx(TableContainer, __assign({
      maxHeight: "38rem"
    }, {
      children: _jsxs(Table, __assign({
        sx: {
          tableLayout: 'auto'
        }
      }, {
        children: [_jsx(TableHead, {
          children: _jsxs(TableRow, {
            children: [_jsx(StyledLevelFirstCell, {
              component: "td",
              stickySizes: LEVEL_ROW_STICKIES,
              "aria-label": LEVEL_ROW_INDENTS.metric,
              sx: {
                width: LEVEL_ROW_STICKIES[0],
                backgroundColor: '#e4e4e4 !important'
              }
            }), daysOfWeek.map(function (day, dayIndex) {
              return _jsx(StyledDailyHeaderCell, __assign({
                align: "center"
              }, {
                children: _jsxs(Stack, __assign({
                  direction: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  spacing: 0.5
                }, {
                  children: [_jsx(StyledDateLink, __assign({
                    onClick: function () {
                      return handleDateClick(day.id);
                    },
                    sx: {
                      color: headerColor
                    }
                  }, {
                    children: day.label
                  })), isDayExceeded(dayIndex) && _jsx(SeatExceededIcon, {
                    onClick: openAlertDetails
                  })]
                }))
              }), day.id);
            }), _jsx(StyledDailyHeaderCell, __assign({
              align: "center"
            }, {
              children: t('daily.totalAverage', {
                defaultValue: 'Total/Average'
              })
            })), _jsx(StyledDailyHeaderCell, __assign({
              align: "center"
            }, {
              children: t('daily.staffHours', {
                defaultValue: 'Staff Hours'
              })
            }))]
          })
        }), _jsx(TableBody, {
          children: _jsx(DailyMetricHierarchyRows, {
            visibleMetrics: visibleMetrics,
            detail: detail,
            countries: countries,
            queuesByLob: queuesByLob,
            bodyColor: bodyColor,
            renderValueCells: renderValueCells,
            renderBlankCells: renderBlankCells
          })
        })]
      }))
    }))]
  });
};
export var DailyDetailTable = function () {
  var _a, _b, _c;
  var navigate = useNavigate();
  var basePlanId = useParams().basePlanId;
  var _d = useSearchParams(),
    searchParams = _d[0],
    setSearchParams = _d[1];
  var _e = useDailyContext(),
    state = _e[0],
    setState = _e[1];
  var dailyData = useDailyAPI()[0];
  var timelines = useReportTimelineOptions();
  var dailyReportData = useDailyReportPlan();
  var _f = usePlanColor(),
    headerColor = _f[0],
    bodyColor = _f[1];
  var visibleMetrics = useVisibleMetrics();
  var daysOfWeek = useDaysOfWeek();
  var handleDateClick = function (dateStr) {
    if (!basePlanId) return;
    navigate(buildIntervalUrl(basePlanId, state, dateStr));
  };
  var handlePrevWeek = function () {
    if ((state === null || state === void 0 ? void 0 : state.month) === undefined || !timelines.length) return;
    var idx = timelines.findIndex(function (timeline) {
      return timeline.id === state.month;
    });
    if (idx > 0) {
      var month_1 = timelines[idx - 1].id;
      setState(function (curr) {
        return curr ? __assign(__assign({}, curr), {
          month: month_1,
          startMonth: month_1,
          endMonth: month_1
        }) : curr;
      });
    }
  };
  var handleNextWeek = function () {
    if ((state === null || state === void 0 ? void 0 : state.month) === undefined || !timelines.length) return;
    var idx = timelines.findIndex(function (timeline) {
      return timeline.id === state.month;
    });
    if (idx >= 0 && idx < timelines.length - 1) {
      var month_2 = timelines[idx + 1].id;
      setState(function (curr) {
        return curr ? __assign(__assign({}, curr), {
          month: month_2,
          startMonth: month_2,
          endMonth: month_2
        }) : curr;
      });
    }
  };
  var currentTimelineLabel = ((_a = timelines.find(function (timeline) {
    return timeline.id === (state === null || state === void 0 ? void 0 : state.month);
  })) === null || _a === void 0 ? void 0 : _a.name) || '';
  var data = dailyData.data;
  if (!isDailyQueueData(data)) return null;
  var detail = data.detail;
  var countries = (_b = data.countries) !== null && _b !== void 0 ? _b : [];
  var queuesByLob = data.queuesByLob;
  return _jsxs(Paper, __assign({
    elevation: 0,
    sx: {
      backgroundColor: theme.palette.background.default
    }
  }, {
    children: [_jsxs(ContentNav, __assign({
      sx: {
        mb: 2
      }
    }, {
      children: [_jsx(TableNavBar, {
        onNext: handleNextWeek,
        onPrev: handlePrevWeek,
        title: currentTimelineLabel,
        disabledNext: (state === null || state === void 0 ? void 0 : state.month) === undefined || timelines.findIndex(function (timeline) {
          return timeline.id === state.month;
        }) >= timelines.length - 1,
        disabledPrev: (state === null || state === void 0 ? void 0 : state.month) === undefined || timelines.findIndex(function (timeline) {
          return timeline.id === state.month;
        }) <= 0
      }), _jsxs(StyledJumpTo, __assign({
        sx: {
          gap: 2
        }
      }, {
        children: [_jsx(StyledAutocomplete, {
          options: dailyReportData || [],
          getOptionValue: function (o) {
            return o.uniqueId;
          },
          value: (state === null || state === void 0 ? void 0 : state.uniqueId) || '',
          onChange: function (val) {
            if (val) {
              setSearchParams(updateUniqueIdSearchParam(searchParams, val), {
                replace: true
              });
            }
          },
          disabled: !(dailyReportData === null || dailyReportData === void 0 ? void 0 : dailyReportData.length)
        }), _jsx(StyledAutocomplete, {
          options: timelines,
          value: (_c = state === null || state === void 0 ? void 0 : state.month) !== null && _c !== void 0 ? _c : '',
          onChange: function (val) {
            if (val !== undefined && val !== null) {
              var month_3 = Number(val);
              setState(function (curr) {
                return curr ? __assign(__assign({}, curr), {
                  month: month_3,
                  startMonth: month_3,
                  endMonth: month_3
                }) : curr;
              });
            }
          },
          disabled: !timelines.length
        })]
      }))]
    })), _jsx(DayTable, {
      daysOfWeek: daysOfWeek,
      handleDateClick: handleDateClick,
      visibleMetrics: visibleMetrics,
      detail: detail,
      countries: countries,
      queuesByLob: queuesByLob,
      headerColor: headerColor,
      bodyColor: bodyColor
    })]
  }));
};